import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { PaymentDetail } from "@codestack/shared";
import { refundSchema } from "@codestack/shared";
import { Alert, Button, Input, Modal, StatusBadge, Textarea } from "@codestack/ui";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { ApiError, apiRequest } from "../lib/api";
import { ErrorState, LoadingState } from "../components/States";
import { dateTime, money, titleCase } from "../lib/format";

export function TransactionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const { data, error, loading, reload } = useApi<PaymentDetail>(
    id ? `/admin/transactions/${id}` : null,
  );

  const [refundOpen, setRefundOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [replaying, setReplaying] = useState(false);

  if (loading && !data) return <LoadingState label="Loading transaction…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  const refundable = data.status === "SUCCESS" || data.status === "PARTIALLY_REFUNDED";
  const refundedMinor = data.refunds
    .filter((refund) => refund.status === "COMPLETED")
    .reduce((sum, refund) => sum + Number(refund.amountMinor), 0);
  const remainingMinor = Number(data.amountMinor) - refundedMinor;

  const openRefund = () => {
    setAmount((remainingMinor / 100).toFixed(2));
    setReason("");
    setFormError(null);
    setRefundOpen(true);
  };

  const submitRefund = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const amountMinor = Math.round(Number(amount) * 100);
    const parsed = refundSchema.safeParse({
      amountMinor,
      reason: reason.trim() || undefined,
    });

    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "Check the refund details.");
      return;
    }

    if (amountMinor > remainingMinor) {
      setFormError("Refund amount exceeds the remaining refundable balance.");
      return;
    }

    setSubmitting(true);
    try {
      await apiRequest(`/admin/transactions/${data.id}/refund`, {
        method: "POST",
        body: parsed.data,
      });
      setRefundOpen(false);
      setNotice("Refund submitted. The provider response will be recorded shortly.");
      reload();
    } catch (cause) {
      setFormError(
        cause instanceof ApiError ? cause.message : "The refund could not be submitted.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const reverify = async () => {
    setVerifying(true);
    setNotice(null);
    try {
      const result = await apiRequest<{ queued: boolean; status: string; reason?: string }>(
        `/admin/transactions/${data.id}/verify`,
        { method: "POST" },
      );
      setNotice(
        result.queued
          ? "Verification queued. The status will update once the worker confirms it with Squad."
          : (result.reason ?? "Nothing to verify."),
      );
      reload();
    } catch (cause) {
      setNotice(
        cause instanceof ApiError ? cause.message : "The verification could not be queued.",
      );
    } finally {
      setVerifying(false);
    }
  };

  const replayCallback = async () => {
    setReplaying(true);
    setNotice(null);
    try {
      const result = await apiRequest<{
        queued: boolean;
        status: string;
        reason?: string;
        eventType?: string;
      }>(`/admin/transactions/${data.id}/replay-callback`, { method: "POST" });
      setNotice(
        result.queued
          ? `Callback queued (${result.eventType ?? "payment event"}). The platform will receive it within a few seconds.`
          : (result.reason ?? "Nothing to replay."),
      );
      reload();
    } catch (cause) {
      setNotice(
        cause instanceof ApiError ? cause.message : "The callback could not be queued.",
      );
    } finally {
      setReplaying(false);
    }
  };

  const settled = ["SUCCESS", "REFUNDED", "PARTIALLY_REFUNDED"].includes(data.status);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link
            to="/transactions"
            className="text-xs text-ink-500 transition-colors hover:text-ink-200"
          >
            ← Transactions
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-lg font-semibold text-white">
              {data.reference}
            </h1>
            <StatusBadge status={data.status} />
          </div>
          <p className="mt-1 text-sm text-ink-400">
            {data.platformName} · {dateTime(data.createdAt)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {!refundable && data.status !== "FAILED" && data.status !== "ABANDONED" ? (
            <Button variant="secondary" onClick={reverify} disabled={verifying}>
              {verifying ? "Verifying…" : "Re-verify with Squad"}
            </Button>
          ) : null}
          {settled ? (
            <Button variant="secondary" onClick={replayCallback} disabled={replaying}>
              {replaying ? "Queuing…" : "Re-send platform callback"}
            </Button>
          ) : null}
          {can("transaction:refund") && refundable ? (
            <Button onClick={openRefund}>Issue refund</Button>
          ) : null}
        </div>
      </div>

      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="admin-card p-5">
          <p className="admin-label">Amount</p>
          <p className="admin-value mt-2">{money(data.amountMinor, data.currency)}</p>
          <p className="mt-1 text-xs text-ink-500">
            Fee {money(data.feeMinor, data.currency)} · Net{" "}
            {money(data.netMinor, data.currency)}
          </p>
        </div>
        <div className="admin-card p-5">
          <p className="admin-label">Refunded</p>
          <p className="admin-value mt-2">{money(refundedMinor, data.currency)}</p>
          <p className="mt-1 text-xs text-ink-500">
            Remaining {money(remainingMinor, data.currency)}
          </p>
        </div>
        <div className="admin-card p-5">
          <p className="admin-label">Provider</p>
          <p className="mt-2 font-mono text-sm text-ink-100">
            {data.providerReference ?? "—"}
          </p>
          <p className="mt-1 text-xs text-ink-500">
            {data.paidAt ? `Paid ${dateTime(data.paidAt)}` : "Not yet paid"}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="admin-card p-5">
          <h2 className="text-sm font-semibold text-white">Payment details</h2>
          <dl className="mt-4 space-y-3 text-sm">
            {[
              ["Order", data.orderReference],
              ["Customer", data.customerEmail ?? "—"],
              ["Currency", data.currency],
              ["Idempotency key", data.idempotencyKey ?? "—"],
              ["Failure reason", data.failureReason ?? "—"],
              ["Updated", dateTime(data.updatedAt)],
            ].map(([label, value]) => (
              <div key={label} className="flex items-start justify-between gap-4">
                <dt className="text-xs text-ink-500">{label}</dt>
                <dd className="max-w-[60%] break-words text-right text-xs text-ink-200">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="admin-card p-5">
          <h2 className="text-sm font-semibold text-white">Attempts</h2>
          {data.attempts.length === 0 ? (
            <p className="mt-4 text-xs text-ink-500">No attempts recorded.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {data.attempts.map((attempt) => (
                <li
                  key={attempt.id}
                  className="flex items-start justify-between gap-3 border-b border-white/[0.05] pb-3 last:border-0 last:pb-0"
                >
                  <div>
                    <p className="text-xs font-medium text-ink-100">
                      {titleCase(attempt.action)}
                    </p>
                    <p className="mt-0.5 text-[11px] text-ink-500">
                      {attempt.message ?? "—"}
                    </p>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={attempt.status} />
                    <p className="mt-1 text-[10px] text-ink-600">
                      {dateTime(attempt.createdAt)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="admin-card p-5">
          <h2 className="text-sm font-semibold text-white">Provider events</h2>
          {data.events.length === 0 ? (
            <p className="mt-4 text-xs text-ink-500">No provider events received.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {data.events.map((event) => (
                <li key={event.id} className="border-b border-white/[0.05] pb-3 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-mono text-[11px] text-ink-200">{event.eventType}</p>
                    <span className="text-[10px] text-ink-600">
                      {dateTime(event.receivedAt)}
                    </span>
                  </div>
                  <p className="mt-1 truncate font-mono text-[10px] text-ink-600">
                    {event.providerEventId}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="admin-card p-5">
          <h2 className="text-sm font-semibold text-white">Ledger entries</h2>
          {data.ledger.length === 0 ? (
            <p className="mt-4 text-xs text-ink-500">No ledger entries yet.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {data.ledger.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-3 border-b border-white/[0.05] pb-3 last:border-0 last:pb-0"
                >
                  <div>
                    <p className="text-xs font-medium text-ink-100">
                      {titleCase(entry.type)}
                    </p>
                    <p className="mt-0.5 text-[10px] text-ink-600">
                      {dateTime(entry.createdAt)}
                    </p>
                  </div>
                  <p
                    className={[
                      "font-mono text-xs",
                      Number(entry.amountMinor) < 0 ? "text-rose-300" : "text-emerald-300",
                    ].join(" ")}
                  >
                    {money(entry.amountMinor, entry.currency)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="admin-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-white">Platform callbacks</h2>
            <p className="mt-1 text-xs text-ink-500">
              Outbound <code className="font-mono">payment.success</code> /{" "}
              <code className="font-mono">payment.refunded</code> deliveries to{" "}
              {data.platformName}. If the platform still shows PENDING, check the
              status and error below.
            </p>
          </div>
          {settled ? (
            <Button variant="secondary" onClick={replayCallback} disabled={replaying}>
              {replaying ? "Queuing…" : "Re-send callback"}
            </Button>
          ) : null}
        </div>

        {!data.platformCallbackUrl ? (
          <div className="mt-4">
            <Alert tone="warning">
              This platform has no callback URL configured, so CodeStack cannot notify
              it of payment outcomes. Set a callback URL on{" "}
              <Link to={`/platforms/${data.platformId}`} className="underline">
                {data.platformName}
              </Link>{" "}
              — the platform will stay PENDING until it either receives a callback or
              polls <code className="font-mono">GET /api/v1/platform/payments/{data.reference}</code>.
            </Alert>
          </div>
        ) : data.webhookDeliveries.length === 0 ? (
          <div className="mt-4">
            <Alert tone="warning">
              No callback has ever been attempted for this payment. The callback job
              either never ran or was dropped before delivery. Use{" "}
              <strong>Re-send callback</strong> above to queue one now.
            </Alert>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-[11px] uppercase tracking-[0.1em] text-ink-500">
                <tr>
                  <th className="pb-2 font-semibold">Event</th>
                  <th className="pb-2 font-semibold">Status</th>
                  <th className="pb-2 font-semibold">Attempts</th>
                  <th className="pb-2 font-semibold">HTTP</th>
                  <th className="pb-2 font-semibold">Last error</th>
                  <th className="pb-2 font-semibold">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {data.webhookDeliveries.map((delivery) => (
                  <tr key={delivery.id}>
                    <td className="py-2.5 font-mono text-[11px] text-ink-200">
                      {delivery.eventType}
                    </td>
                    <td className="py-2.5">
                      <StatusBadge status={delivery.status} />
                    </td>
                    <td className="py-2.5 font-mono text-xs text-ink-300">
                      {delivery.attempts}
                    </td>
                    <td className="py-2.5 font-mono text-xs text-ink-300">
                      {delivery.lastStatusCode ?? "—"}
                    </td>
                    <td className="max-w-[22rem] truncate py-2.5 text-xs text-rose-300">
                      {delivery.lastError ?? "—"}
                    </td>
                    <td className="py-2.5 text-xs text-ink-500">
                      {dateTime(delivery.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="admin-card p-5">
        <h2 className="text-sm font-semibold text-white">Refunds</h2>
        {data.refunds.length === 0 ? (
          <p className="mt-4 text-xs text-ink-500">No refunds issued.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-[11px] uppercase tracking-[0.1em] text-ink-500">
                <tr>
                  <th className="pb-2 font-semibold">Reference</th>
                  <th className="pb-2 font-semibold">Amount</th>
                  <th className="pb-2 font-semibold">Status</th>
                  <th className="pb-2 font-semibold">Reason</th>
                  <th className="pb-2 font-semibold">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {data.refunds.map((refund) => (
                  <tr key={refund.id}>
                    <td className="py-2.5 font-mono text-[11px] text-ink-200">
                      {refund.reference}
                    </td>
                    <td className="py-2.5 font-mono text-xs text-ink-200">
                      {money(refund.amountMinor, data.currency)}
                    </td>
                    <td className="py-2.5">
                      <StatusBadge status={refund.status} />
                    </td>
                    <td className="py-2.5 text-xs text-ink-400">{refund.reason ?? "—"}</td>
                    <td className="py-2.5 text-xs text-ink-500">
                      {dateTime(refund.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        title="Issue refund"
        description={`Refund up to ${money(remainingMinor, data.currency)} on ${data.reference}.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRefundOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitRefund}
              loading={submitting}
              disabled={submitting}
            >
              Submit refund
            </Button>
          </>
        }
      >
        <form onSubmit={submitRefund} className="space-y-4">
          {formError ? <Alert tone="danger">{formError}</Alert> : null}
          <Input
            label={`Amount (${data.currency})`}
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <Textarea
            label="Reason"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Duplicate charge, customer request…"
          />
          <p className="text-xs leading-relaxed text-ink-500">
            Refunds are sent to the provider immediately and cannot be reversed once
            completed. The action is recorded in the audit log.
          </p>
        </form>
      </Modal>
    </div>
  );
}
